import React, { useEffect, useState } from "react";
import TriggerList from "./TriggerList";
import CompanyIcDashboard from "./CompanyIcDashboard";
import AdminUsersPage from "./AdminUsersPage";
import AdminPipelineControlsPage from "./AdminPipelineControlsPage";
import AdminAgentsPage from "./AdminAgentsPage";
import PipelineHistoryPage from "./PipelineHistoryPage";
import SetPasswordPage from "./SetPasswordPage";
import TriggerOutcomesPage from "./TriggerOutcomesPage";
import {
  apiFetch,
  clearAuthToken,
  getApiBase,
  parseJsonOrExplain,
  setAuthToken,
} from "./apiBase";

function App() {
  const isSetPasswordPath =
    typeof window !== "undefined" && window.location.pathname.startsWith("/set-password");
  const [view, setView] = useState("triggers");
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    apiFetch(`${getApiBase()}/auth/me`)
      .then((res) => {
        if (res.status === 401) return null;
        return parseJsonOrExplain(res);
      })
      .then((me) => setUser(me))
      .catch(() => setUser(null))
      .finally(() => setAuthReady(true));
  }, []);

  const doLogin = (e) => {
    e.preventDefault();
    setLoginError(null);
    setLoginBusy(true);
    fetch(`${getApiBase()}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: loginEmail.trim(),
        password: loginPassword,
      }),
    })
      .then((res) => parseJsonOrExplain(res))
      .then((data) => {
        setAuthToken(data.token);
        setUser(data.user);
      })
      .catch((err) => {
        setLoginError(err.message || "Inloggning misslyckades");
      })
      .finally(() => setLoginBusy(false));
  };

  const doLogout = () => {
    clearAuthToken();
    setUser(null);
    setLoginPassword("");
  };

  const navButtonStyle = (active) => ({
    padding: "10px 18px",
    borderRadius: "999px",
    border: active ? "1px solid #111" : "1px solid rgba(17,17,17,0.2)",
    background: active ? "#111" : "#fff",
    color: active ? "#fff" : "#111",
    cursor: "pointer",
    fontSize: "14px",
  });

  if (!authReady) {
    return (
      <div style={{ minHeight: "100vh", background: "#f7f5f1", padding: "48px" }}>
        <p style={{ color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>
          Laddar...
        </p>
      </div>
    );
  }

  if (isSetPasswordPath) {
    return <SetPasswordPage onAuthed={(u) => setUser(u)} />;
  }

  if (!user) {
    return (
      <div style={{ minHeight: "100vh", background: "#f7f5f1", padding: "32px" }}>
        <div
          style={{
            maxWidth: "420px",
            margin: "64px auto",
            background: "#fff",
            border: "1px solid rgba(17,17,17,0.12)",
            borderRadius: "18px",
            padding: "30px",
            fontFamily: "Arial, Helvetica, sans-serif",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "22px" }}>
            <img
              src="/images/alphahunterlogo.png"
              alt="Alpha Hunter"
              style={{ height: "48px", width: "auto", maxWidth: "100%", objectFit: "contain", display: "inline-block" }}
            />
          </div>
          <h1 style={{ margin: "0 0 18px 0", fontSize: "30px", fontFamily: 'Georgia, "Times New Roman", serif' }}>
            Logga in
          </h1>
          <form onSubmit={doLogin}>
            <label style={{ display: "block", fontSize: "13px", marginBottom: "6px" }}>E-post</label>
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid rgba(17,17,17,0.2)",
                boxSizing: "border-box",
                marginBottom: "12px",
              }}
            />
            <label style={{ display: "block", fontSize: "13px", marginBottom: "6px" }}>Lösenord</label>
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid rgba(17,17,17,0.2)",
                boxSizing: "border-box",
                marginBottom: "14px",
              }}
            />
            {loginError && (
              <p style={{ color: "#b42318", marginTop: 0, marginBottom: "10px", fontSize: "14px" }}>
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loginBusy}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "999px",
                border: "none",
                background: "#111",
                color: "#fff",
                cursor: loginBusy ? "wait" : "pointer",
              }}
            >
              {loginBusy ? "Loggar in..." : "Logga in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f7f5f1",
        color: "#111",
        fontFamily: 'Georgia, "Times New Roman", serif',
      }}
    >
      <div
        style={{
          maxWidth: "1440px",
          margin: "0 auto",
          padding: "40px 32px 64px 32px",
        }}
      >
        <header
          style={{
            marginBottom: "36px",
            paddingBottom: "24px",
            borderBottom: "1px solid rgba(17,17,17,0.12)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
              <img
                src="/images/alphahunterlogo.png"
                alt=""
                aria-hidden
                style={{
                  height: "88px",
                  width: "auto",
                  maxWidth: "min(400px, 75vw)",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  fontSize: "12px",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  color: "#5f5a52",
                  lineHeight: 1.35,
                }}
              >
                PEQ Alpha Hunter
              </div>
            </div>
            <button
              type="button"
              onClick={doLogout}
              style={{
                padding: "8px 12px",
                borderRadius: "999px",
                border: "1px solid rgba(17,17,17,0.2)",
                background: "#fff",
                cursor: "pointer",
                fontSize: "13px",
                fontFamily: "Arial, Helvetica, sans-serif",
                marginLeft: "auto",
              }}
            >
              Logga ut ({user.email})
            </button>
          </div>

          <h1
            style={{
              fontSize: "64px",
              lineHeight: 1,
              fontWeight: 500,
              margin: "0 0 14px 0",
              letterSpacing: "-0.03em",
            }}
          >
            Deal Engine Dashboard
          </h1>

          <p
            style={{
              maxWidth: "820px",
              margin: "0 0 20px 0",
              fontSize: "18px",
              lineHeight: 1.6,
              color: "#3f3a34",
              fontFamily: 'Arial, Helvetica, sans-serif',
            }}
          >
            Följ triggers genom tratten, förstå varför de går vidare eller
            stoppas, och få en tydlig vy över kopplade bolag och beslutshistorik.
          </p>

          <nav
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              fontFamily: 'Arial, Helvetica, sans-serif',
            }}
          >
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button type="button" onClick={() => setView("triggers")} style={navButtonStyle(view === "triggers")}>
                Triggers
              </button>
              <button type="button" onClick={() => setView("trigger_outcomes")} style={navButtonStyle(view === "trigger_outcomes")}>
                Triggerutfall
              </button>
              <button type="button" onClick={() => setView("companies_ic")} style={navButtonStyle(view === "companies_ic")}>
                Bolag (IC)
              </button>
              <button type="button" onClick={() => setView("pipeline_history")} style={navButtonStyle(view === "pipeline_history")}>
                Historik
              </button>
            </div>
            {user?.role === "admin" && (
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginLeft: "auto" }}>
                <button type="button" onClick={() => setView("admin_agents")} style={navButtonStyle(view === "admin_agents")}>
                  Agenter
                </button>
                <button type="button" onClick={() => setView("admin_pipeline")} style={navButtonStyle(view === "admin_pipeline")}>
                  Pipelinekontroller
                </button>
                <button type="button" onClick={() => setView("admin_users")} style={navButtonStyle(view === "admin_users")}>
                  Admin
                </button>
              </div>
            )}
          </nav>
        </header>

        {view === "triggers" && <TriggerList />}
        {view === "companies_ic" && <CompanyIcDashboard user={user} />}
        {view === "trigger_outcomes" && <TriggerOutcomesPage user={user} />}
        {view === "admin_users" && user?.role === "admin" && <AdminUsersPage currentUser={user} />}
        {view === "admin_pipeline" && user?.role === "admin" && <AdminPipelineControlsPage />}
        {view === "admin_agents" && user?.role === "admin" && <AdminAgentsPage />}
        {view === "pipeline_history" && <PipelineHistoryPage />}
      </div>
    </div>
  );
}

export default App;
